import { CanAccess, useGetIdentity, useTranslate } from "ra-core";
import { CreateButton } from "@/components/admin/create-button";
import { DataTable } from "@/components/admin/data-table";
import { ExportButton } from "@/components/admin/export-button";
import { List } from "@/components/admin/list";
import { ReferenceField } from "@/components/admin/reference-field";
import { SelectField } from "@/components/admin/select-field";
import { SelectInput } from "@/components/admin/select-input";
import { SearchInput } from "@/components/admin/search-input";
import { FilterButton } from "@/components/admin/filter-form";

import { DataImportButton } from "../dataImport/DataImportButton";
import { OnlyMineInput } from "../deals/OnlyMineInput";
import { TopToolbar } from "../layout/TopToolbar";
import type { Client, Sale } from "../types";
import { CLIENT_TYPE_CHOICES } from "./clientChoices";

/**
 * Almikia Epic E3b (TASK-064), owner answer A5: "Client search by name,
 * phone or email is allowed (always limited by RLS to what each user can
 * see)". This lifts the earlier no-search rule (Q5) for these three fields
 * only — RFC and CURP are never search targets (see the Supabase provider's
 * `clients` `q` mapping).
 */
export const ClientList = () => {
  const { identity } = useGetIdentity();

  if (!identity) return null;

  const clientFilters = [
    <SearchInput source="q" alwaysOn />,
    <SelectInput
      source="client_type"
      label={false}
      alwaysOn
      emptyText="resources.clients.fields.client_type"
      choices={CLIENT_TYPE_CHOICES}
    />,
    <OnlyMineInput
      source="sales_id"
      label="resources.clients.filters.only_mine"
      alwaysOn
    />,
  ];

  return (
    <List
      sort={{ field: "full_legal_name", order: "ASC" }}
      perPage={25}
      filters={clientFilters}
      actions={<ClientListActions />}
    >
      <DataTable rowClick="show">
        <DataTable.Col source="full_legal_name" />
        <DataTable.Col source="client_type">
          <SelectField source="client_type" choices={CLIENT_TYPE_CHOICES} />
        </DataTable.Col>
        <DataTable.Col
          source="sales_id"
          label="resources.clients.fields.sales_id"
        >
          <ReferenceField<Client, Sale>
            source="sales_id"
            reference="sales"
            link={false}
            render={({ referenceRecord }) =>
              referenceRecord
                ? `${referenceRecord.first_name} ${referenceRecord.last_name}`
                : null
            }
          />
        </DataTable.Col>
        <DataTable.Col source="email" />
        <DataTable.Col source="phone" />
      </DataTable>
    </List>
  );
};

const ClientListActions = () => {
  const translate = useTranslate();
  return (
    <TopToolbar>
      <FilterButton />
      <CanAccess resource="clients" action="export">
        <ExportButton />
      </CanAccess>
      <CanAccess resource="clients" action="create">
        <DataImportButton resource="clients" />
        {/*
          This screen's single main action (design-system.md section 6/8):
          creating a client is the next step from this list, so it opts into
          the gradient primary style; every other action here (filter,
          export, import) stays outline/secondary.
        */}
        <CreateButton
          variant="gradient"
          label={translate("resources.clients.action.new", {
            _: "New Client",
          })}
        />
      </CanAccess>
    </TopToolbar>
  );
};
